/**
 * Тарифы (см. `TARIFFS_SPEC.md`).
 *
 * Состав:
 *   - `rates_base`     — одна строка, 5 услуг × 2 стороны.
 *   - `rates_k1`       — географический коэффициент.
 *   - `rates_k2`       — диапазоны оценочной цены.
 *   - `idle_settings`  — параметры тарификации простоя.
 *
 * RPC `validate_rates_k2` (если появится на бэке) пока не вызывается;
 * клиентская валидация целостности активных диапазонов реализована в
 * `validateK2Coverage` и применяется до записи в БД.
 */
import { mockState } from '@/api/mock-data'
import { USE_MOCK } from '@/config'
import { createClient } from '@/api/supabase'
import i18n from '@/utils/i18n'
import type {
  IdleSetting,
  IdleSettingStatus,
  RateK1,
  RateK2,
  RatesBase,
  RatesBasePatch,
} from '@/types/tariffs'

// ---------------------------------------------------------------------------
// rates_base — §6.1
// ---------------------------------------------------------------------------

export async function getRatesBase(): Promise<RatesBase> {
  if (USE_MOCK) return structuredClone(mockState.ratesBase)
  const supabase = createClient()
  const { data, error } = await supabase
    .from('rates_base')
    .select('*')
    .eq('id', 1)
    .single()
  if (error) throw new Error(error.message)
  return data as RatesBase
}

export async function updateRatesBase(patch: RatesBasePatch): Promise<RatesBase> {
  for (const [k, v] of Object.entries(patch)) {
    if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) {
      throw new Error(i18n.t('apiErr.tariffs.fieldNonNegative', { k }))
    }
  }
  if (USE_MOCK) {
    const now = new Date().toISOString()
    mockState.ratesBase = { ...mockState.ratesBase, ...patch, id: 1, updated_at: now }
    return structuredClone(mockState.ratesBase)
  }
  const supabase = createClient()
  const { data, error } = await supabase
    .from('rates_base')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', 1)
    .select()
    .single()
  if (error) throw new Error(error.message)
  return data as RatesBase
}

// ---------------------------------------------------------------------------
// idle_settings — §6.5
// ---------------------------------------------------------------------------

export async function listIdleSettings(): Promise<IdleSetting[]> {
  if (USE_MOCK) return structuredClone(mockState.idleSettings)
  const supabase = createClient()
  const { data, error } = await supabase.from('idle_settings').select('*').order('status')
  if (error) throw new Error(error.message)
  return (data ?? []) as IdleSetting[]
}

export interface IdleSettingPatch {
  grace_minutes?: number
  round_to_minutes?: number
}

export async function updateIdleSetting(
  status: IdleSettingStatus,
  patch: IdleSettingPatch,
): Promise<IdleSetting> {
  if (patch.grace_minutes != null && (!Number.isInteger(patch.grace_minutes) || patch.grace_minutes < 0)) {
    throw new Error(i18n.t('apiErr.tariffs.graceNonNegInteger'))
  }
  if (
    patch.round_to_minutes != null &&
    (!Number.isInteger(patch.round_to_minutes) || patch.round_to_minutes <= 0)
  ) {
    throw new Error(i18n.t('apiErr.tariffs.roundingPositiveInteger'))
  }
  if (USE_MOCK) {
    const i = mockState.idleSettings.findIndex((s) => s.status === status)
    if (i < 0) throw new Error(i18n.t('apiErr.tariffs.idleStatusNotFound', { status }))
    mockState.idleSettings[i] = {
      ...mockState.idleSettings[i],
      ...patch,
      status,
      updated_at: new Date().toISOString(),
    }
    return structuredClone(mockState.idleSettings[i])
  }
  const supabase = createClient()
  const { data, error } = await supabase
    .from('idle_settings')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('status', status)
    .select()
    .single()
  if (error) throw new Error(error.message)
  return data as IdleSetting
}

// ---------------------------------------------------------------------------
// rates_k1 — §6.2
// ---------------------------------------------------------------------------

export async function listRatesK1(): Promise<RateK1[]> {
  if (USE_MOCK) {
    return [...mockState.ratesK1].sort((a, b) => a.city.localeCompare(b.city, 'ru'))
  }
  const supabase = createClient()
  const { data, error } = await supabase
    .from('rates_k1')
    .select('*')
    .order('city', { ascending: true })
  if (error) throw new Error(error.message)
  return (data ?? []) as RateK1[]
}

export interface RateK1Input {
  city: string
  coefficient: number
  is_active?: boolean
}

function validateK1Input(input: RateK1Input): void {
  if (!input.city.trim()) throw new Error(i18n.t('apiErr.tariffs.enterCity'))
  if (!Number.isFinite(input.coefficient) || input.coefficient <= 0) {
    throw new Error(i18n.t('apiErr.tariffs.coefficientPositive'))
  }
}

export async function createRateK1(input: RateK1Input): Promise<RateK1> {
  validateK1Input(input)
  const city = input.city.trim()
  if (USE_MOCK) {
    if (mockState.ratesK1.some((r) => r.city.toLowerCase() === city.toLowerCase())) {
      throw new Error(i18n.t('apiErr.tariffs.cityExists', { city }))
    }
    const now = new Date().toISOString()
    const row: RateK1 = {
      id: `k1-${crypto.randomUUID().slice(0, 8)}`,
      city,
      coefficient: input.coefficient,
      is_active: input.is_active ?? true,
      created_at: now,
      updated_at: now,
    }
    mockState.ratesK1.push(row)
    return structuredClone(row)
  }
  const supabase = createClient()
  const { data, error } = await supabase
    .from('rates_k1')
    .insert({
      city,
      coefficient: input.coefficient,
      is_active: input.is_active ?? true,
    })
    .select()
    .single()
  if (error) throw new Error(error.message)
  return data as RateK1
}

export interface RateK1Patch {
  city?: string
  coefficient?: number
  is_active?: boolean
}

export async function updateRateK1(id: string, patch: RateK1Patch): Promise<RateK1> {
  if (patch.city != null && !patch.city.trim()) throw new Error(i18n.t('apiErr.tariffs.enterCity'))
  if (patch.coefficient != null && (!Number.isFinite(patch.coefficient) || patch.coefficient <= 0)) {
    throw new Error(i18n.t('apiErr.tariffs.coefficientPositive'))
  }
  if (USE_MOCK) {
    const i = mockState.ratesK1.findIndex((r) => r.id === id)
    if (i < 0) throw new Error(i18n.t('apiErr.tariffs.k1NotFound'))
    if (patch.city != null) {
      const newCity = patch.city.trim()
      if (
        mockState.ratesK1.some(
          (r) => r.id !== id && r.city.toLowerCase() === newCity.toLowerCase(),
        )
      ) {
        throw new Error(i18n.t('apiErr.tariffs.cityExists', { city: newCity }))
      }
      patch.city = newCity
    }
    mockState.ratesK1[i] = {
      ...mockState.ratesK1[i],
      ...patch,
      id,
      updated_at: new Date().toISOString(),
    }
    return structuredClone(mockState.ratesK1[i])
  }
  const supabase = createClient()
  const { data, error } = await supabase
    .from('rates_k1')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()
  if (error) throw new Error(error.message)
  return data as RateK1
}

// ---------------------------------------------------------------------------
// rates_k2 — §6.3
// ---------------------------------------------------------------------------

export async function listRatesK2(): Promise<RateK2[]> {
  if (USE_MOCK) {
    return [...mockState.ratesK2].sort((a, b) => a.range_from - b.range_from)
  }
  const supabase = createClient()
  const { data, error } = await supabase
    .from('rates_k2')
    .select('*')
    .order('range_from', { ascending: true })
  if (error) throw new Error(error.message)
  return (data ?? []) as RateK2[]
}

export interface RateK2Input {
  range_from: number
  range_to: number | null
  coefficient: number
  is_active?: boolean
}

function validateK2InputShape(input: RateK2Input | RateK2Patch): void {
  if ('range_from' in input && input.range_from != null) {
    if (!Number.isFinite(input.range_from) || input.range_from < 0) {
      throw new Error(i18n.t('apiErr.tariffs.fromGteZero'))
    }
  }
  if ('range_to' in input && input.range_to != null) {
    if (!Number.isFinite(input.range_to)) {
      throw new Error(i18n.t('apiErr.tariffs.toNumberOrInf'))
    }
  }
  if (
    'range_from' in input &&
    'range_to' in input &&
    input.range_from != null &&
    input.range_to != null
  ) {
    if (input.range_to <= input.range_from) {
      throw new Error(i18n.t('apiErr.tariffs.toGreaterThanFrom'))
    }
  }
  if ('coefficient' in input && input.coefficient != null) {
    if (!Number.isFinite(input.coefficient) || input.coefficient <= 0) {
      throw new Error(i18n.t('apiErr.tariffs.coefficientPositive'))
    }
  }
}

export async function createRateK2(input: RateK2Input): Promise<RateK2> {
  validateK2InputShape(input)
  const now = new Date().toISOString()
  if (USE_MOCK) {
    const draft: RateK2 = {
      id: `k2-${crypto.randomUUID().slice(0, 8)}`,
      range_from: input.range_from,
      range_to: input.range_to,
      coefficient: input.coefficient,
      is_active: input.is_active ?? true,
      created_at: now,
      updated_at: now,
    }
    mockState.ratesK2.push(draft)
    return structuredClone(draft)
  }
  const supabase = createClient()
  const { data, error } = await supabase
    .from('rates_k2')
    .insert({
      range_from: input.range_from,
      range_to: input.range_to,
      coefficient: input.coefficient,
      is_active: input.is_active ?? true,
    })
    .select()
    .single()
  if (error) throw new Error(error.message)
  return data as RateK2
}

export interface RateK2Patch {
  range_from?: number
  range_to?: number | null
  coefficient?: number
  is_active?: boolean
}

export async function updateRateK2(id: string, patch: RateK2Patch): Promise<RateK2> {
  validateK2InputShape(patch)
  if (USE_MOCK) {
    const i = mockState.ratesK2.findIndex((r) => r.id === id)
    if (i < 0) throw new Error(i18n.t('apiErr.tariffs.k2NotFound'))
    const merged: RateK2 = {
      ...mockState.ratesK2[i],
      ...patch,
      id,
      updated_at: new Date().toISOString(),
    }
    if (merged.range_to != null && merged.range_to <= merged.range_from) {
      throw new Error(i18n.t('apiErr.tariffs.toGreaterThanFrom'))
    }
    mockState.ratesK2[i] = merged
    return structuredClone(merged)
  }
  const supabase = createClient()
  const { data, error } = await supabase
    .from('rates_k2')
    .update({
      ...patch,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single()
  if (error) throw new Error(error.message)
  return data as RateK2
}
